/**
 * AgreementsSubTab: list of signed legal agreements for this property.
 *
 * Ported from the Agreements section of DocumentsTab on 2026-05-29 as
 * part of the Documents-tab dissolution. Read flow + rendering kept
 * intact; only the import paths and the standalone wrapper changed.
 */

import { useCallback, useEffect, useState } from 'react';
import { formatDateMedium } from '../../../../../../utils/formatters';
import type { SupabaseClient } from '@supabase/supabase-js';
import LoadingSpinner from '../../../../../../components/LoadingSpinner';
import EmptyState from '../../../../../../components/EmptyState';
import { StatusBadge } from '../../../../../../components/DataTable';
import { PdfModal } from '../../../../../../components/PdfModal';
import { signPosDocument } from '../../../../../../lib/posDocuments';
import type { EnrichedProperty } from '../types';

interface Agreement {
  id: string;
  agreement_type: string | null;
  agreement_version: string | null;
  tier_selected: string | null;
  signed_name: string | null;
  signed_title: string | null;
  signed_at: string | null;
  created_at: string | null;
  pdf_url: string | null;
}

interface AgreementsSubTabProps {
  supabase: SupabaseClient;
  property: EnrichedProperty;
}

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  return formatDateMedium(s);
}

export default function AgreementsSubTab({ supabase, property }: AgreementsSubTabProps) {
  const [agreements, setAgreements] = useState<Agreement[]>([]);
  const [loading, setLoading] = useState(true);
  const [pdfPreview, setPdfPreview] = useState<{ url: string; title: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('property_agreements')
      .select('id, agreement_type, agreement_version, tier_selected, signed_name, signed_title, signed_at, created_at, pdf_url')
      .eq('property_id', property.id)
      .order('signed_at', { ascending: false });
    setAgreements((data as Agreement[]) || []);
    setLoading(false);
  }, [supabase, property.id]);

  useEffect(() => { void load(); }, [load]);

  const openPdf = async (path: string) => {
    const url = await signPosDocument(supabase, path);
    if (!url) {
      alert('Could not open the agreement PDF.');
      return;
    }
    setPdfPreview({ url, title: 'Agreement' });
  };

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center' }}><LoadingSpinner /></div>;

  if (agreements.length === 0) {
    return <EmptyState message="No signed agreements recorded yet." />;
  }

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
        {agreements.map((a, idx) => (
          <div key={a.id} style={{
            padding: '1rem 1.25rem',
            borderBottom: idx < agreements.length - 1 ? '1px solid var(--color-border)' : 'none',
            display: 'flex', flexDirection: 'column', gap: '0.5rem',
          }}>
            <div className="flex items-start justify-between flex-wrap gap-lg">
              <div className="flex items-center flex-wrap gap-md">
                <span style={{ fontWeight: 600, fontSize: 'var(--text-lg)' }}>
                  {a.signed_name || 'Unknown signatory'}
                  {a.signed_title ? ` · ${a.signed_title}` : ''}
                </span>
                <StatusBadge bg="var(--color-success-bg)" color="var(--color-success)">Signed</StatusBadge>
              </div>
              {a.pdf_url && (
                <button type="button" onClick={() => void openPdf(a.pdf_url!)}
                  className="btn btn-secondary" style={{ fontSize: 'var(--text-sm)', padding: '0.375rem 0.75rem' }}>
                  View PDF
                </button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.5rem 1.5rem', fontSize: 'var(--text-md)' }}>
              <div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.025em', marginBottom: '0.125rem' }}>Signed</div>
                <div className="fw-500">{formatDate(a.signed_at)}</div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.025em', marginBottom: '0.125rem' }}>Type</div>
                <div className="fw-500">{a.agreement_type || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.025em', marginBottom: '0.125rem' }}>Version</div>
                <div className="fw-500">{a.agreement_version || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textTransform: 'uppercase', letterSpacing: '0.025em', marginBottom: '0.125rem' }}>Tier</div>
                <div style={{ fontWeight: 500, textTransform: 'capitalize' }}>{a.tier_selected || '—'}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {pdfPreview && (
        <PdfModal url={pdfPreview.url} title={pdfPreview.title} onClose={() => setPdfPreview(null)} />
      )}
    </div>
  );
}
